import { useEffect, useState } from 'react'

import { getSolanaTransactionReceipt } from '@src/chains/solana/utils'
import {
    BACKOFF_MULTIPLIER,
    INITIAL_POLLING_INTERVAL,
    MAXIMUM_RETRY_COUNT,
    MAX_POLLING_INTERVAL,
} from '@src/constants/exchange'
import { ResultCode } from '@src/enums'
import { exchangeLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'

export const useWaitForTransactionSolana = () => {
    const [retryCount, setRetryCount] = useState<number>(0)
    const [transactionHash, setTransactionHash] = useState<string>('')
    const updateHistory = useHistoryStore(state => state.updateHistory)

    const waitForTransactionReceipt = async (hash: string) => {
        return new Promise((resolve, reject) => {
            setTransactionHash(hash)

            const attemptGetReceipt = async () => {
                if (retryCount >= MAXIMUM_RETRY_COUNT) {
                    updateHistory(null, {
                        result: {
                            sourceTxHash: ``,
                            message: 'We are so sorry that this order failed. Please try again.',
                            status: ResultCode.failure,
                        },
                    })
                    reject({ message: 'Exceeded maximum retry count' })
                    return
                }

                try {
                    const response = await getSolanaTransactionReceipt(hash)
                    exchangeLogger.info('wait tx response:', { response })
                    if (response) {
                        const receipt = {
                            blockHash: `0x${response.slot.toString()}`,
                            blockNumber: BigInt(response.slot.valueOf()),
                            contractAddress: undefined,
                            cumulativeGasUsed: BigInt(0),
                            effectiveGasPrice: BigInt(0),
                            from: '0x',
                            gasUsed: BigInt(0),
                            logs: [],
                            logsBloom: '0x',
                            status: 'success',
                            to: null,
                            transactionHash: '0x',
                            transactionIndex: 0,
                            type: 'legacy',
                            chainId: 0,
                        }
                        resolve(receipt)
                        return
                    }

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, retryCount),
                        MAX_POLLING_INTERVAL
                    )
                    setRetryCount(prev => prev + 1)
                    setTimeout(attemptGetReceipt, nextInterval)
                } catch (error) {
                    exchangeLogger.error('getSolanaTransactionReceipt error:', { error })
                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, retryCount),
                        MAX_POLLING_INTERVAL
                    )
                    setRetryCount(prev => prev + 1)
                    setTimeout(attemptGetReceipt, nextInterval)
                }
            }

            attemptGetReceipt()
        })
    }

    useEffect(() => {
        setRetryCount(0)
    }, [transactionHash])

    return { waitForTransactionReceipt }
}
